import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, cant, buscar } from '../utils/formato';

// Vender.
//
// Es la pantalla que más se usa y muchas veces se usa con el cliente delante y
// una mano ocupada. Por eso: se busca escribiendo, se toca el producto, se pone
// la cantidad y ya. Nada de menús anidados.
//
// La instalación va en la misma venta que los materiales porque así ocurre en
// la realidad: se vende la teja y se cobra ponerla. Pero se suman aparte,
// porque son dos negocios con márgenes muy distintos.

const FORMAS = [
  ['efectivo',      '💵 Efectivo'],
  ['transferencia', '🏦 Transferencia'],
  ['mixto',         '🔀 Mixto'],
  ['credito',       '📝 Fiado'],
];

function Vender({ onCambio }) {
  const [productos, setProductos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [busqueda, setBusqueda] = useState('');

  const [carrito, setCarrito] = useState([]);
  const [clienteId, setClienteId] = useState('');
  const [formaPago, setFormaPago] = useState('efectivo');
  const [efectivo, setEfectivo] = useState('');
  const [transferencia, setTransferencia] = useState('');

  const [midiendo, setMidiendo] = useState(null);   // producto que se está agregando
  const [cantidad, setCantidad] = useState('');
  const [precioLinea, setPrecioLinea] = useState('');

  const [instalando, setInstalando] = useState(false);
  const [manoObra, setManoObra] = useState({
    descripcion: '', precio: '', mano_obra: '', trabajador_id: '' });
  const [instaladores, setInstaladores] = useState([]);

  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);

  const cargar = useCallback(() => {
    axios.get('/api/inventario').then(r => setProductos(r.data || [])).catch(() => {});
    axios.get('/api/clientes').then(r => setClientes(r.data || [])).catch(() => {});
    axios.get('/api/trabajadores').then(r => setInstaladores(r.data || [])).catch(() => {});
  }, []);

  useEffect(cargar, [cargar]);

  const cliente = clientes.find(c => String(c.id) === String(clienteId));
  const encontrados = buscar(productos, busqueda).slice(0, busqueda ? 30 : 12);

  const totalMateriales = carrito.filter(l => l.tipo === 'material')
    .reduce((s, l) => s + l.total, 0);
  const totalInstalacion = carrito.filter(l => l.tipo === 'instalacion')
    .reduce((s, l) => s + l.total, 0);
  const total = totalMateriales + totalInstalacion;

  // Cuánto deja esta venta. Se ve ANTES de cobrar, que es cuando todavía se
  // puede ajustar un precio; después ya es una cifra en un informe.
  const costo = carrito.filter(l => l.tipo === 'material')
    .reduce((s, l) => s + l.cantidad * (l.costo || 0), 0);
  // La mano de obra de la instalación también es costo de esta venta. Sin
  // restarla, una obra donde el instalador se lleva casi todo se vería igual de
  // buena que una que uno mismo hace.
  const costoObra = carrito.filter(l => l.tipo === 'instalacion')
    .reduce((s, l) => s + (l.mano_obra || 0), 0);
  const ganancia = total - costo - costoObra;

  const empezar = (p) => {
    setMidiendo(p);
    setCantidad('');
    setPrecioLinea(String(Math.round(p.precio_venta || 0)));
  };

  const agregar = (e) => {
    e.preventDefault();
    const c = parseFloat(cantidad);
    const pr = parseFloat(precioLinea);
    if (!(c > 0)) return;
    if (!(pr >= 0)) return;

    setCarrito([...carrito, {
      clave: Date.now(),
      tipo: 'material',
      producto_id: midiendo.id,
      nombre: midiendo.nombre,
      codigo: midiendo.codigo,
      unidad: midiendo.unidad,
      costo: midiendo.costo_unitario,
      existencia: midiendo.existencia,
      cantidad: c,
      precio: pr,
      total: Math.round(c * pr),
    }]);
    setMidiendo(null);
    setBusqueda('');
    setAviso(null);
  };

  const agregarManoObra = (e) => {
    e.preventDefault();
    const pr = parseFloat(manoObra.precio);
    if (!(pr > 0)) return alert('Falta cuánto se cobra por la instalación');
    // Lo que se le paga al instalador es el costo de ESTA obra. Puede ir en
    // cero cuando la hace uno mismo: entonces la instalación es toda ganancia,
    // y eso también es cierto.
    const mo = Math.max(0, parseFloat(manoObra.mano_obra) || 0);
    setCarrito([...carrito, {
      clave: Date.now(),
      tipo: 'instalacion',
      descripcion: manoObra.descripcion || 'Instalación',
      cantidad: 1,
      precio: pr,
      costo: mo,
      mano_obra: mo,
      trabajador_id: manoObra.trabajador_id || null,
      total: Math.round(pr),
    }]);
    setInstalando(false);
    setManoObra({ descripcion: '', precio: '', mano_obra: '', trabajador_id: '' });
  };

  const quitar = (clave) => setCarrito(carrito.filter(l => l.clave !== clave));

  const limpiar = () => {
    setCarrito([]); setClienteId(''); setFormaPago('efectivo');
    setEfectivo(''); setTransferencia(''); setBusqueda('');
  };

  const cobrar = async () => {
    if (carrito.length === 0) return;
    if (formaPago === 'credito' && !clienteId) {
      return setAviso({ tipo: 'malo', texto: 'Para fiar hay que decir a qué cliente' });
    }
    if (formaPago === 'mixto') {
      const suma = (parseFloat(efectivo) || 0) + (parseFloat(transferencia) || 0);
      if (Math.abs(suma - total) > 1) {
        return setAviso({
          tipo: 'malo',
          texto: `El efectivo y la transferencia suman ${plata(suma)} y la venta es de ${plata(total)}`,
        });
      }
    }

    setGuardando(true);
    setAviso(null);
    try {
      const { data } = await axios.post('/api/ventas', {
        cliente_id: clienteId ? parseInt(clienteId) : null,
        forma_pago: formaPago,
        monto_efectivo: parseFloat(efectivo) || 0,
        monto_transferencia: parseFloat(transferencia) || 0,
        lineas: carrito.map(l => ({
          tipo: l.tipo,
          producto_id: l.producto_id || null,
          descripcion: l.descripcion || null,
          cantidad: l.cantidad,
          precio: l.precio,
          mano_obra: l.mano_obra || 0,
          trabajador_id: l.trabajador_id || null,
        })),
      });
      setAviso({
        tipo: 'bueno',
        texto: `Venta de ${plata(data.total)} registrada` +
               (data.total_instalacion > 0
                 ? ` — ${plata(data.total_materiales)} en materiales y ${plata(data.total_instalacion)} de instalación.`
                 : '.') +
               ` Dejó ${plata(data.ganancia)}.`,
      });
      limpiar();
      cargar();
      onCambio && onCambio();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo registrar la venta' });
    }
    setGuardando(false);
  };

  return (
    <>
      <div className="encabezado">
        <h1>Vender</h1>
        <p>Materiales, instalación, o las dos cosas</p>
      </div>

      {aviso && <div className={'aviso ' + aviso.tipo}>{aviso.texto}</div>}

      <div className="bloque">
        <h2>🔎 Buscar producto</h2>
        <div className="cuerpo">
          <div className="buscador">
            <input
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              placeholder="Código, nombre o referencia — «tor», «3/8», «zinc»…"
            />
            {busqueda && (
              <button type="button" onClick={() => setBusqueda('')} title="Limpiar">×</button>
            )}
          </div>

          {encontrados.length === 0 ? (
            <div className="vacio">
              {busqueda ? `Ningún producto coincide con «${busqueda}»`
                        : 'Todavía no hay productos. Créalos en la pantalla de Productos.'}
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 9,
                          gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' }}>
              {encontrados.map(p => {
                const sinNada = p.existencia <= 0;
                return (
                  <button
                    key={p.id}
                    onClick={() => empezar(p)}
                    style={{
                      padding: '12px 13px', textAlign: 'left', cursor: 'pointer',
                      borderRadius: 'var(--radio)', font: 'inherit',
                      border: '1px solid var(--raya)', background: 'var(--tarjeta)',
                      boxShadow: 'var(--sombra)', opacity: sinNada ? 0.6 : 1,
                    }}
                  >
                    <div className="cifra" style={{ fontSize: 11, color: 'var(--tinta-tenue)' }}>
                      {p.codigo}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 14, margin: '2px 0 4px',
                                  lineHeight: 1.25 }}>
                      {p.nombre}
                    </div>
                    <div className="cifra" style={{ color: 'var(--acero)', fontWeight: 700 }}>
                      {plata(p.precio_venta)}
                      <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--tinta-tenue)' }}>
                        {' / '}{p.unidad}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, marginTop: 4,
                                  color: sinNada ? 'var(--malo)'
                                       : p.alerta === 'bajo' ? 'var(--ojo)' : 'var(--tinta-tenue)' }}>
                      {sinNada ? 'agotado' : `quedan ${cant(p.existencia, p.unidad)}`}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Cuánto lleva */}
          {midiendo && (
            <form onSubmit={agregar} style={{
              marginTop: 15, padding: 15, borderRadius: 'var(--radio)',
              background: 'var(--acero-piso)', border: '1px solid var(--acero)',
            }}>
              <div style={{ fontWeight: 700, marginBottom: 11 }}>
                {midiendo.nombre}
                <span style={{ fontWeight: 400, color: 'var(--tinta-tenue)' }}>
                  {' · '}quedan {cant(midiendo.existencia, midiendo.unidad)}
                </span>
              </div>
              <div className="campos">
                <label className="campo">
                  <span>Cuántas ({midiendo.unidad}) *</span>
                  <input type="number" step="0.01" min="0" className="num-grande"
                         required autoFocus value={cantidad}
                         onChange={e => setCantidad(e.target.value)} placeholder="0" />
                </label>
                <label className="campo">
                  <span>Precio por {midiendo.unidad} *</span>
                  <input type="number" step="1" min="0" className="num-grande" required
                         value={precioLinea} onChange={e => setPrecioLinea(e.target.value)} />
                </label>
              </div>

              {parseFloat(cantidad) > 0 && parseFloat(precioLinea) >= 0 && (
                <div style={{ marginTop: 11, fontSize: 15 }}>
                  {cant(cantidad, midiendo.unidad)} × {plata(precioLinea)} ={' '}
                  <strong className="cifra">
                    {plata(parseFloat(cantidad) * parseFloat(precioLinea))}
                  </strong>
                  {parseFloat(cantidad) > midiendo.existencia && (
                    <div style={{ color: 'var(--ojo)', marginTop: 6, fontWeight: 600 }}>
                      ⚠️ Solo quedan {cant(midiendo.existencia, midiendo.unidad)}. Se puede
                      vender igual, pero el inventario va a quedar en negativo.
                    </div>
                  )}
                  {parseFloat(precioLinea) < midiendo.costo_unitario && (
                    <div style={{ color: 'var(--malo)', marginTop: 6, fontWeight: 600 }}>
                      ⚠️ Ese precio está por debajo de lo que costó
                      ({plata(midiendo.costo_unitario)}). Se pierde en cada unidad.
                    </div>
                  )}
                </div>
              )}

              <div className="acciones">
                <button type="button" className="boton suave" onClick={() => setMidiendo(null)}>
                  Cancelar
                </button>
                <button className="boton">➕ Agregar</button>
              </div>
            </form>
          )}

          {!midiendo && !instalando && (
            <div className="acciones">
              <button className="boton suave" onClick={() => setInstalando(true)}>
                🔧 Cobrar una instalación
              </button>
            </div>
          )}

          {/* Mano de obra */}
          {instalando && (
            <form onSubmit={agregarManoObra} style={{
              marginTop: 15, padding: 15, borderRadius: 'var(--radio)',
              background: 'var(--obra-piso)', border: '1px solid var(--obra)',
            }}>
              <div style={{ fontWeight: 700, marginBottom: 11 }}>🔧 Instalación</div>
              <div className="campos">
                <label className="campo">
                  <span>Qué se hizo</span>
                  <input value={manoObra.descripcion} autoFocus
                         placeholder="Instalación de 60 m² de cubierta"
                         onChange={e => setManoObra({ ...manoObra, descripcion: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Cuánto se cobra *</span>
                  <input type="number" step="1" min="0" className="num-grande" required
                         value={manoObra.precio}
                         onChange={e => setManoObra({ ...manoObra, precio: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Cuánto le pago al instalador</span>
                  <input type="number" step="1" min="0" className="num-grande"
                         value={manoObra.mano_obra} placeholder="0 si la hago yo"
                         onChange={e => setManoObra({ ...manoObra, mano_obra: e.target.value })} />
                </label>
                {instaladores.length > 0 && (
                  <label className="campo">
                    <span>Quién la hace</span>
                    <select value={manoObra.trabajador_id}
                            onChange={e => setManoObra({ ...manoObra, trabajador_id: e.target.value })}>
                      <option value="">— Yo mismo —</option>
                      {instaladores.map(t => (
                        <option key={t.id} value={t.id}>{t.nombre}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>

              {/* Lo que deja la obra, antes de cerrarla. Después ya es un dato
                  en un informe y el precio ya se pactó. */}
              {parseFloat(manoObra.precio) > 0 && (() => {
                const cobra = parseFloat(manoObra.precio) || 0;
                const paga = parseFloat(manoObra.mano_obra) || 0;
                const queda = cobra - paga;
                const pct = cobra > 0 ? Math.round(queda / cobra * 100) : 0;
                return (
                  <div style={{ marginTop: 12, fontSize: 14 }}>
                    Esta obra te deja{' '}
                    <strong className="cifra"
                            style={{ color: queda <= 0 ? 'var(--malo)' : undefined }}>
                      {plata(queda)}
                    </strong>{' '}
                    <span style={{ color: 'var(--tinta-tenue)' }}>({pct}% de lo que cobras)</span>
                    {queda < 0 && (
                      <div className="aviso malo" style={{ marginTop: 8, marginBottom: 0 }}>
                        Le estás pagando al instalador más de lo que cobras por el trabajo.
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="acciones">
                <button type="button" className="boton suave" onClick={() => setInstalando(false)}>
                  Cancelar
                </button>
                <button className="boton">➕ Agregar</button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* La cuenta */}
      {carrito.length > 0 && (
        <div className="bloque">
          <h2>
            🧾 La cuenta
            <span className="contador">
              {carrito.length} {carrito.length === 1 ? 'línea' : 'líneas'}
            </span>
          </h2>
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Qué</th>
                  <th style={{ textAlign: 'right' }}>Cantidad</th>
                  <th style={{ textAlign: 'right' }}>Precio</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {carrito.map(l => (
                  <tr key={l.clave}>
                    <td className="recio">
                      {l.tipo === 'instalacion' ? (
                        <>
                          <span className="pinta obra">Instalación</span>{' '}
                          {l.descripcion}
                          {l.mano_obra > 0 && (
                            <div style={{ fontSize: 11, fontWeight: 400,
                                          color: 'var(--tinta-tenue)', marginTop: 3 }}>
                              instalador {plata(l.mano_obra)} · deja {plata(l.total - l.mano_obra)}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {l.nombre}
                          <div className="cifra" style={{ fontSize: 11, fontWeight: 400,
                                                          color: 'var(--tinta-tenue)' }}>
                            {l.codigo}
                          </div>
                        </>
                      )}
                    </td>
                    <td className="num">
                      {l.tipo === 'instalacion' ? '—' : cant(l.cantidad, l.unidad)}
                    </td>
                    <td className="num">{plata(l.precio)}</td>
                    <td className="num recio">{plata(l.total)}</td>
                    <td>
                      <button className="boton chico suave" onClick={() => quitar(l.clave)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="cuerpo" style={{ borderTop: '1px solid var(--raya)' }}>
            {totalInstalacion > 0 && (
              <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginBottom: 14,
                            fontSize: 14, color: 'var(--tinta-media)' }}>
                <span>Materiales <strong className="cifra">{plata(totalMateriales)}</strong></span>
                <span>Instalación <strong className="cifra">{plata(totalInstalacion)}</strong></span>
              </div>
            )}

            <div className="campos">
              <label className="campo">
                <span>Cómo paga</span>
                <select value={formaPago} onChange={e => setFormaPago(e.target.value)}>
                  {FORMAS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                </select>
              </label>
              <label className="campo">
                <span>Cliente {formaPago === 'credito' ? '*' : '(opcional)'}</span>
                <select value={clienteId} onChange={e => setClienteId(e.target.value)}>
                  <option value="">— Sin cliente —</option>
                  {clientes.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.nombre}{c.telefono ? ` · ${c.telefono}` : ''}
                    </option>
                  ))}
                </select>
              </label>

              {formaPago === 'mixto' && (
                <>
                  <label className="campo">
                    <span>💵 En efectivo</span>
                    <input type="number" step="1" min="0" className="num-grande"
                           value={efectivo} onChange={e => setEfectivo(e.target.value)} />
                  </label>
                  <label className="campo">
                    <span>🏦 Por transferencia</span>
                    <input type="number" step="1" min="0" className="num-grande"
                           value={transferencia}
                           onChange={e => setTransferencia(e.target.value)} />
                  </label>
                </>
              )}
            </div>

            {formaPago === 'mixto' && (efectivo || transferencia) && (
              (() => {
                const suma = (parseFloat(efectivo) || 0) + (parseFloat(transferencia) || 0);
                const falta = total - suma;
                return (
                  <div className={'aviso ' + (Math.abs(falta) <= 1 ? 'bueno' : 'ojo')}
                       style={{ marginTop: 14, marginBottom: 0 }}>
                    {Math.abs(falta) <= 1
                      ? '✓ Los dos montos suman el total'
                      : falta > 0
                        ? `Faltan ${plata(falta)} por repartir`
                        : `Sobran ${plata(-falta)}: los montos se pasan del total`}
                  </div>
                );
              })()
            )}

            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                          gap: 16, flexWrap: 'wrap', marginTop: 18 }}>
              <div>
                <div style={{ fontSize: 12, color: 'var(--tinta-tenue)' }}>Total a cobrar</div>
                <div className="cifra" style={{ fontSize: 30, fontWeight: 800,
                                                letterSpacing: '-.03em', color: 'var(--venta)' }}>
                  {plata(total)}
                </div>
                {/* Lo que deja, ANTES de cobrar: es cuando todavía se puede
                    ajustar un precio. Después ya es una cifra en un informe. */}
                <div style={{ fontSize: 13, marginTop: 4,
                              color: ganancia >= 0 ? 'var(--tinta-tenue)' : 'var(--malo)' }}>
                  {ganancia >= 0
                    ? <>deja {plata(ganancia)}</>
                    : <>⚠️ pierde {plata(-ganancia)}</>}
                  {formaPago === 'credito' && cliente && ` · queda debiendo ${cliente.nombre}`}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="boton suave" onClick={limpiar}>Vaciar</button>
                <button className="boton venta grande" disabled={guardando} onClick={cobrar}>
                  {guardando ? 'Guardando…'
                    : formaPago === 'credito' ? '📝 Anotar fiado' : '💵 Cobrar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <UltimasVentas onCambio={onCambio} />
    </>
  );
}

// El historial va aparte para que recargarlo no repinte el carrito a medio
// llenar.
function UltimasVentas({ onCambio }) {
  const [ventas, setVentas] = useState([]);

  const cargar = useCallback(() => {
    axios.get('/api/ventas').then(r => setVentas(r.data || [])).catch(() => {});
  }, []);
  useEffect(cargar, [cargar]);

  const anular = async (v) => {
    if (!window.confirm(
      `¿Anular la venta de ${plata(v.total)}?\n\n` +
      'Los materiales vuelven al inventario y la venta deja de contar en las ganancias.'
    )) return;
    try {
      await axios.post(`/api/ventas/${v.id}/anular`);
      cargar();
      onCambio && onCambio();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo anular');
    }
  };

  if (ventas.length === 0) return null;

  const pinta = (v) => {
    if (v.anulada) return <span className="pinta malo">Anulada</span>;
    if (v.forma_pago === 'efectivo') return <span className="pinta bueno">💵 Efectivo</span>;
    if (v.forma_pago === 'transferencia') return <span className="pinta acero">🏦 Transferencia</span>;
    if (v.forma_pago === 'mixto') return <span className="pinta gris">🔀 Mixto</span>;
    return <span className="pinta ojo">📝 Fiado</span>;
  };

  return (
    <div className="bloque">
      <h2>📋 Últimas ventas <span className="contador">{ventas.length}</span></h2>
      <div className="tabla-caja">
        <table className="datos">
          <thead>
            <tr>
              <th>Cuándo</th>
              <th>Cliente</th>
              <th>Qué llevó</th>
              <th>Pago</th>
              <th style={{ textAlign: 'right' }}>Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {ventas.slice(0, 40).map(v => (
              <tr key={v.id} style={{ opacity: v.anulada ? .45 : 1 }}>
                <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                  {new Date(v.fecha).toLocaleDateString('es-CO',
                    { day: 'numeric', month: 'short' })}
                  <div style={{ color: 'var(--tinta-tenue)', fontSize: 11 }}>
                    {new Date(v.fecha).toLocaleTimeString('es-CO',
                      { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </td>
                <td>{v.cliente || <span style={{ color: 'var(--tinta-tenue)' }}>—</span>}</td>
                <td style={{ fontSize: 13 }}>
                  {v.lineas.map(l => (
                    <div key={l.id}>
                      {l.tipo === 'instalacion'
                        ? <>🔧 {l.descripcion}</>
                        : <>{l.producto} · {cant(l.cantidad, l.unidad)}</>}
                    </div>
                  ))}
                </td>
                <td>
                  {pinta(v)}
                  {v.forma_pago === 'mixto' && !v.anulada && (
                    <div style={{ fontSize: 11, color: 'var(--tinta-tenue)', marginTop: 2 }}>
                      {plata(v.monto_efectivo)} + {plata(v.monto_transferencia)}
                    </div>
                  )}
                </td>
                <td className="num recio">
                  {plata(v.total)}
                  {v.total_instalacion > 0 && (
                    <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--obra)' }}>
                      {plata(v.total_instalacion)} de obra
                    </div>
                  )}
                </td>
                <td>
                  {!v.anulada && (
                    <button className="boton chico suave" onClick={() => anular(v)}>Anular</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Vender;
